import { useState, useEffect, useCallback } from 'react'
import {
  Sparkles,
  Wand2,
  Loader2,
  RefreshCw,
  Check,
  ImageIcon,
  Lock,
  GraduationCap,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from './ui/button'
import { Textarea } from './ui/textarea'
import { resolveImageUrl, currencySymbol, getApiUrl, formatMoney } from '../lib/utils'
import { pollPaymentStatus } from '../lib/payments'
import { usePaymentOverlay } from '../contexts/PaymentOverlayContext'
import {
  VIBES,
  getAiQuota,
  generateAiDesign,
  purchaseAiCredits,
  loadFonts,
  fontStack,
} from '../lib/aiDesign'
import { getAiTutorial } from '../lib/aiTutorial'
import { localizeEventType } from '../lib/eventTypes'

const API = getApiUrl()

// The event-type tutorial auto-opens until the host dismisses it once; after
// that it stays available behind the "How it works" toggle.
const AI_TUTORIAL_DISMISSED_KEY = 'storyfy_ai_tutorial_dismissed'

/**
 * AI invitation designer used inside the Create/Edit wizard's Design step.
 *
 * Props:
 *   context  - { title, event_type, language } used to steer the generation
 *   applied  - { font_heading, font_body, cover_image } currently on the form,
 *              so we can show which generated design is live
 *   onApply  - called with { cover_image, font_heading, font_body, accent_color }
 *   appLang  - 'en' | 'ka' for the UI copy
 */
const AiDesignStudio = ({ context, applied, onApply, appLang = 'en' }: any) => {
  const t = (en: string, ka: string) => (appLang === 'ka' ? ka : en)

  const [quota, setQuota] = useState<any>(null)
  const [vibe, setVibe] = useState('elegant')
  const [prompt, setPrompt] = useState('')
  const [generating, setGenerating] = useState(false)
  const [result, setResult] = useState<any>(null)
  const [purchasing, setPurchasing] = useState(false)
  const { open: openPaymentOverlay } = usePaymentOverlay()

  // Event-type-tailored tutorial: auto-open on first visit, reopenable later.
  const [showTutorial, setShowTutorial] = useState(() => {
    try {
      return !localStorage.getItem(AI_TUTORIAL_DISMISSED_KEY)
    } catch {
      return true
    }
  })
  const tutorial = getAiTutorial(context?.event_type)
  const dismissTutorial = () => {
    setShowTutorial(false)
    try {
      localStorage.setItem(AI_TUTORIAL_DISMISSED_KEY, '1')
    } catch {
      /* storage unavailable — non-critical */
    }
  }

  const loadQuota = useCallback(async () => {
    try {
      setQuota(await getAiQuota())
    } catch {
      setQuota(null)
    }
  }, [])

  useEffect(() => {
    loadQuota()
  }, [loadQuota])

  const freeLeft = quota?.free_remaining ?? 0
  const credits = quota?.paid_credits ?? 0
  const price = quota?.price ?? 2
  const sym = currencySymbol(quota?.currency || 'GEL')
  const canGenerateFree = freeLeft > 0 || credits > 0

  const runGenerate = useCallback(async () => {
    setGenerating(true)
    try {
      const design = await generateAiDesign({
        title: context?.title,
        event_type: context?.event_type,
        language: context?.language || appLang,
        vibe,
        prompt: prompt.trim() || undefined,
      })
      loadFonts([design.font_heading, design.font_body])
      setResult(design)
      setQuota((q: any) => ({
        ...(q || {}),
        free_remaining: design.free_remaining,
        paid_credits: design.paid_credits,
      }))
      return true
    } catch (err: any) {
      if (err?.response?.status === 402) {
        // Out of free tries — offer to buy a single generation.
        return 'payment'
      }
      toast.error(
        err?.response?.data?.detail ||
          t('Could not generate a design. Try again.', 'დიზაინი ვერ შეიქმნა. სცადეთ თავიდან.'),
      )
      return false
    } finally {
      setGenerating(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, vibe, prompt, appLang])

  // After a credit purchase clears, refresh the quota and continue the generation
  // the user was trying to run — the credit they just bought funds it.
  const continueAfterPaid = useCallback(async () => {
    await loadQuota()
    const outcome = await runGenerate()
    if (outcome === 'payment') {
      toast.error(t('Payment not yet confirmed.', 'გადახდა ჯერ არ დადასტურდა.'))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadQuota, runGenerate, appLang])

  // Buy one generation through the shared checkout: sandbox
  // confirms instantly; a live Flitt gateway opens the hosted page in the in-page
  // overlay and we poll until the payment clears.
  const startPurchase = useCallback(async () => {
    setPurchasing(true)
    try {
      const data = await purchaseAiCredits(1)
      if (data.checkout_url) {
        openPaymentOverlay(data.checkout_url)
        toast.message(
          t('Complete the payment in the secure window', 'დაასრულეთ გადახდა უსაფრთხო ფანჯარაში'),
        )
        pollPaymentStatus({
          url: `${API}/public/payments/${data.payment_id}/status`,
          isDone: (d) => d.status === 'paid',
          onDone: () => continueAfterPaid(),
        })
      } else {
        // Sandbox: already paid + credited.
        await continueAfterPaid()
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.detail ||
          t('Could not start checkout.', 'გადახდის დაწყება ვერ მოხერხდა.'),
      )
    } finally {
      setPurchasing(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appLang, openPaymentOverlay, continueAfterPaid])

  const handleGenerateClick = async () => {
    const outcome = await runGenerate()
    if (outcome === 'payment') startPurchase()
  }

  const applyDesign = () => {
    if (!result) return
    onApply({
      cover_image: result.background_url,
      font_heading: result.font_heading,
      font_body: result.font_body,
      accent_color: result.accent,
    })
    toast.success(t('Design applied to your invitation', 'დიზაინი დაემატა მოსაწვევს'))
  }

  const isApplied =
    result &&
    applied?.cover_image === result.background_url &&
    applied?.font_heading === result.font_heading

  return (
    <div className="rounded-bubble border border-[#8b5cf6]/20 bg-gradient-to-br from-[#ff4d8d]/[0.06] via-[#8b5cf6]/[0.04] to-transparent p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[linear-gradient(135deg,#ffe0ec,#e9defb)] text-[#6d28d9]">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <p className="font-display text-sm font-semibold text-[#1b1033] dark:text-[#f4f0ff]">
              {t('AI invitation designer', 'AI მოსაწვევის დიზაინერი')}
            </p>
            <p className="text-xs text-[#4a3d66] dark:text-[#c3b8dd]">
              {t(
                'Generate a background + matching fonts',
                'შექმენი ფონი + შესაბამისი შრიფტები',
              )}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {/* Reopen the tutorial after it's been dismissed */}
          {!showTutorial && (
            <button
              type="button"
              onClick={() => setShowTutorial(true)}
              className="flex items-center gap-1 rounded-full bg-[#1b1033]/[0.05] px-2.5 py-1 text-[11px] font-medium text-[#4a3d66] transition-colors hover:bg-[#1b1033]/10 dark:bg-white/10 dark:text-[#c3b8dd] dark:hover:bg-white/20"
              data-testid="ai-tutorial-toggle"
            >
              <GraduationCap className="h-3 w-3" />
              {t('How it works', 'როგორ მუშაობს')}
            </button>
          )}
          {/* Quota chip */}
          {quota && (
            <span
              className="rounded-full bg-[#1b1033]/[0.05] px-2.5 py-1 text-[11px] font-medium text-[#4a3d66] dark:bg-white/10 dark:text-[#c3b8dd]"
              data-testid="ai-quota"
            >
              {freeLeft > 0
                ? t(`${freeLeft} free left`, `${freeLeft} უფასო დარჩა`)
                : credits > 0
                  ? t(`${credits} credits`, `${credits} კრედიტი`)
                  : `${sym}${formatMoney(price)}/${t('design', 'დიზაინი')}`}
            </span>
          )}
        </div>
      </div>

      {/* Event-type-based tutorial — tailored tips, vibes and example prompts */}
      {showTutorial && (
        <div
          className="mb-3 rounded-2xl border border-[#1b1033]/10 bg-white/70 p-3 dark:border-white/10 dark:bg-white/5"
          data-testid="ai-tutorial"
        >
          <div className="mb-1.5 flex items-start justify-between gap-2">
            <p className="text-xs font-semibold text-[#1b1033] dark:text-[#f4f0ff]">
              <GraduationCap className="mr-1 inline h-3.5 w-3.5 text-[#8b5cf6]" />
              {context?.event_type
                ? t(
                    `Designing for a ${localizeEventType(context.event_type, 'en')}`,
                    `დიზაინი — ${localizeEventType(context.event_type, 'ka')}`,
                  )
                : t('How the AI designer works', 'როგორ მუშაობს AI დიზაინერი')}
            </p>
            <button
              type="button"
              onClick={dismissTutorial}
              className="shrink-0 rounded-full p-0.5 text-[#4a3d66] transition-colors hover:bg-[#1b1033]/[0.06] hover:text-[#1b1033] dark:hover:bg-white/10 dark:hover:text-white"
              aria-label={t('Dismiss tutorial', 'დახურვა')}
              data-testid="ai-tutorial-dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <p className="mb-2 text-[11px] leading-relaxed text-[#4a3d66] dark:text-[#c3b8dd]">
            {tutorial.tip[appLang] || tutorial.tip.en}
          </p>
          {/* Recommended vibes — one tap applies them to the picker below */}
          <div className="mb-2 flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] font-medium uppercase tracking-wide text-[#4a3d66] dark:text-[#c3b8dd]">
              {t('Try these vibes:', 'სცადე ეს განწყობები:')}
            </span>
            {tutorial.vibes.map((vid) => {
              const v = VIBES.find((x) => x.id === vid)
              if (!v) return null
              return (
                <button
                  key={vid}
                  type="button"
                  onClick={() => setVibe(vid)}
                  className={`rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors ${
                    vibe === vid
                      ? 'bg-grad-hot text-white shadow-celebrate-sm'
                      : 'bg-[#8b5cf6]/10 text-[#6d28d9] hover:bg-[#8b5cf6]/20'
                  }`}
                  data-testid={`ai-tutorial-vibe-${vid}`}
                >
                  {v.emoji} {v.label[appLang] || v.label.en}
                </button>
              )
            })}
          </div>
          {/* Example prompts — one tap fills the prompt box */}
          <div className="space-y-1">
            <span className="text-[10px] font-medium uppercase tracking-wide text-[#4a3d66] dark:text-[#c3b8dd]">
              {t('Example prompts — tap to use:', 'მაგალითები — დააჭირე გამოსაყენებლად:')}
            </span>
            {tutorial.prompts.map((p, i) => {
              const text = p[appLang] || p.en
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => setPrompt(text)}
                  className="block w-full truncate rounded-lg bg-[#1b1033]/[0.04] px-2 py-1 text-left text-[11px] italic text-[#4a3d66] transition-colors hover:bg-[#1b1033]/[0.08] dark:bg-white/5 dark:text-[#c3b8dd] dark:hover:bg-white/10"
                  data-testid={`ai-tutorial-prompt-${i}`}
                >
                  “{text}”
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Vibe picker */}
      <p className="mb-2 text-xs font-medium text-[#1b1033]/70 dark:text-[#c3b8dd]">
        {t('Choose a vibe', 'აირჩიე განწყობა')}
      </p>
      <div className="mb-3 flex flex-wrap gap-2">
        {VIBES.map((v) => (
          <button
            key={v.id}
            type="button"
            onClick={() => setVibe(v.id)}
            className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
              vibe === v.id
                ? 'bg-grad-hot text-white shadow-celebrate-sm'
                : 'bg-[#1b1033]/[0.05] text-[#4a3d66] hover:bg-[#1b1033]/10 dark:bg-white/10 dark:text-[#c3b8dd] dark:hover:bg-white/20'
            }`}
            data-testid={`ai-vibe-${v.id}`}
          >
            <span className="mr-1">{v.emoji}</span>
            {v.label[appLang] || v.label.en}
          </button>
        ))}
      </div>

      {/* Optional prompt */}
      <Textarea
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        rows={2}
        maxLength={240}
        placeholder={t(
          'Optional: describe a detail — e.g. "olive branches, warm dusk, gold accents"',
          'არასავალდებულო: აღწერე დეტალი — მაგ. „ზეთისხილის რტოები, თბილი მზის ჩასვლა, ოქროსფერი აქცენტები“',
        )}
        className="mb-3 resize-none rounded-2xl text-sm"
        data-testid="ai-prompt"
      />

      {/* Generate */}
      <Button
        type="button"
        onClick={handleGenerateClick}
        disabled={generating || purchasing || !quota}
        className="w-full rounded-full bg-grad-hot font-bold text-white shadow-celebrate-cta transition-all ease-spring hover:shadow-celebrate-md motion-safe:hover:-translate-y-0.5 motion-safe:active:scale-95"
        data-testid="ai-generate-btn"
      >
        {generating ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t('Designing…', 'იქმნება…')}
          </>
        ) : (
          <>
            <Wand2 className="mr-2 h-4 w-4" />
            {result
              ? t('Regenerate', 'თავიდან შექმნა')
              : t('Generate design', 'დიზაინის შექმნა')}
            {quota && (
              <span className="ml-2 opacity-90">
                {canGenerateFree
                  ? freeLeft > 0
                    ? `· ${freeLeft} ${t('free', 'უფასო')}`
                    : `· ${credits} ${t('credits', 'კრედიტი')}`
                  : `· ${sym}${formatMoney(price)}`}
              </span>
            )}
          </>
        )}
      </Button>

      {!canGenerateFree && quota && (
        <p className="mt-2 flex items-center justify-center gap-1 text-[11px] text-[#4a3d66] dark:text-[#c3b8dd]">
          <Lock className="h-3 w-3" />
          {t(
            `Free tries used — next design is ${sym}${formatMoney(price)}, charged securely.`,
            `უფასო ცადებები ამოიწურა — შემდეგი დიზაინი ${sym}${formatMoney(price)}, უსაფრთხო გადახდით.`,
          )}
        </p>
      )}

      {/* Result preview */}
      {result && (
        <div className="mt-4 overflow-hidden rounded-2xl border border-[#1b1033]/10 shadow-celebrate-sm dark:border-white/10">
          {/* Background with sample text in the chosen fonts */}
          <div className="relative h-40">
            <img
              src={resolveImageUrl(result.background_url)}
              alt=""
              className="h-full w-full object-cover"
              data-testid="ai-preview-bg"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-4 text-center">
              <p
                className="truncate text-2xl font-semibold text-white"
                style={{ fontFamily: fontStack(result.font_heading, 'heading') }}
              >
                {context?.title || t("You're invited", 'მოწვეული ხართ')}
              </p>
              <p
                className="mt-0.5 text-sm text-white/85"
                style={{ fontFamily: fontStack(result.font_body, 'body') }}
              >
                {t('Join us to celebrate', 'შემოგვიერთდით ზეიმზე')}
              </p>
            </div>
          </div>
          {/* Meta row */}
          <div className="flex items-center justify-between gap-3 bg-white p-3 dark:bg-[#1e1438]">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span
                  className="h-4 w-4 shrink-0 rounded-full ring-1 ring-black/10 dark:ring-white/20"
                  style={{ backgroundColor: result.accent }}
                  title={result.accent}
                />
                <p className="truncate text-xs text-[#4a3d66] dark:text-[#c3b8dd]">
                  {result.font_heading} · {result.font_body}
                </p>
              </div>
              {result.rationale && (
                <p className="mt-1 line-clamp-1 text-[11px] text-[#4a3d66] dark:text-[#c3b8dd]">
                  {result.rationale}
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={handleGenerateClick}
                disabled={generating || purchasing}
                className="h-9 w-9 rounded-full"
                title={t('Regenerate', 'თავიდან')}
                data-testid="ai-regenerate-btn"
              >
                <RefreshCw className={`h-4 w-4 ${generating ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                type="button"
                onClick={applyDesign}
                disabled={isApplied}
                className={`rounded-full font-semibold ${
                  isApplied
                    ? 'bg-green-600 text-white hover:bg-green-600'
                    : 'bg-[#1b1033] text-white hover:bg-[#1b1033]/90 dark:bg-white dark:text-[#1b1033]'
                }`}
                data-testid="ai-apply-btn"
              >
                {isApplied ? (
                  <>
                    <Check className="mr-1.5 h-4 w-4" />
                    {t('Applied', 'დაემატა')}
                  </>
                ) : (
                  t('Use this design', 'ამის გამოყენება')
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Empty hint */}
      {!result && (
        <div className="mt-3 flex items-center gap-2 text-[11px] text-[#4a3d66] dark:text-[#c3b8dd]">
          <ImageIcon className="h-3.5 w-3.5" />
          {quota?.provider === 'mock'
            ? t(
                'Preview mode — connect an OpenAI key for photoreal backgrounds.',
                'წინასწარი რეჟიმი — დააკავშირე OpenAI გასაღები ფოტორეალისტური ფონებისთვის.',
              )
            : t(
                'Each design is unique — regenerate until it feels right.',
                'თითოეული დიზაინი უნიკალურია — შექმენი თავიდან სანამ მოგეწონება.',
              )}
        </div>
      )}
    </div>
  )
}

export default AiDesignStudio
